import { getApiUrl } from "@shared/configuration/aws-configuration";
import {
	getApiVersionSetting,
	getEnvironmentSetting,
	useSettings,
} from "@/composables/settings";
import {
	initClient,
	isUserGroupsAuthorized,
	setCustomerId,
	setOrganizationId,
	useCustomerId,
} from "@ptx-autonomous/libs/api";
import { initFlightpathServiceClient } from "@ptx-autonomous/libs/flightpath-service-api";
import {
	callbackHandler,
	johnDeereAuthGuard,
} from "@ptx-autonomous/libs/johndeere";
import { initMachineCertificateClient } from "@ptx-autonomous/libs/machine-certificate-api";
import { initDataExchangeServiceClient } from "@shared/data-exchange-service";
import { userGroupPermissionsSettings } from "@shared/permissions-settings";
import {
	createRouter,
	createWebHistory,
	type NavigationGuard,
} from "vue-router";
import ResourceDocumentsView from "../views/ResourceDocumentsView.vue";
import { allowGuidInQueryParam } from "@shared/utils/query-params";
import {
	authenticate as authenticateFarmEngage,
	isAuthenticated,
	startFarmEngageAuthFlow,
} from "@ptx-autonomous/libs/farmengage";

const matchUUID = (param: string) =>
	`:${param}([0-9a-fA-F]{8}\\b-[0-9a-fA-F]{4}\\b-[0-9a-fA-F]{4}\\b-[0-9a-fA-F]{4}\\b-[0-9a-fA-F]{12})`;

const isUserHasPermission: NavigationGuard = async (to, _from, next) => {
	try {
		switch (to.name) {
			case "switch-organization":
				if (
					!(await isUserGroupsAuthorized(
						userGroupPermissionsSettings.allowedGroupsToSwitchOrganization ?? [
							"FlightPathAdmin",
						],
					))
				) {
					next("/");
					throw new Error("Not authorized");
				}
				break;
			case "machine-update":
				if (
					!(await isUserGroupsAuthorized(
						userGroupPermissionsSettings.allowedGroupsToSoftwareUpdate ?? [
							"FlightPathAdmin",
						],
					))
				) {
					next("/");
					throw new Error("Not authorized");
				}
				break;
			default:
				next("/");
				throw new Error("Not authorized");
				break;
		}

		const environment = getEnvironmentSetting();
		const apiVersion = getApiVersionSetting();
		initClient(false, "", "", getApiUrl(environment, apiVersion));
		next();
	} catch {
		// prevent navigation to the next page user is not a flightpathadmin
		next("/");
	}
};

export const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes: [
		{
			path: "/auth",
			name: "auth",
			component: () => import("../views/AuthView.vue"),
		},
		{
			path: "/docs",
			name: "docs",
			component: () => import("../views/DocsView.vue"),
		},
		{
			path: "/",
			component: () => import("../views/AppLayout.vue"),
			children: [
				{
					path: `home/${matchUUID("machineId")}?`,
					name: "home",
					component: () => import("../views/HomeView.vue"),
				},

				{
					path: "users",
					name: "users",
					component: () => import("../views/UsersView.vue"),
				},
				{
					path: "alarms",
					name: "alarms",
					component: () => import("../views/ActiveAlarmView.vue"),
				},
				{
					path: "machines",
					name: "machines",
					component: () =>
						import("../views/updates/MachinesView.vue"),
				},
				{
					path: `machine-update/${matchUUID("machineId")}?`,
					name: "machine-update",
					component: () =>
						import("../views/updates/MachineUpdateView.vue"),
					beforeEnter: [isUserHasPermission],
				},
				{
					path: `machine-software/${matchUUID("machineId")}`,
					name: "machine-software",
					component: () =>
						import("../views/updates/MachineSoftwareView.vue"),
				},
				{
					path: `machine-logs/${matchUUID("machineId")}`,
					name: "machine-logs",
					component: () =>
						import("../views/updates/MachineSystemLogs.vue"),
				},
				{
					path: "diagnostics",
					name: "diagnostics",
					component: () =>
						import("../views/diagnostics/DiagnosticsView.vue"),
					redirect: {
						name: "diagnostics-main",
					},
					children: [
						{
							path: "",
							name: "diagnostics-main",
							component: () =>
								import("../views/diagnostics/DiagnosticsDataView.vue"),
						},
						{
							path: `support-request/${matchUUID("id")}`,
							name: "diagnostics-support-request",
							component: () =>
								import("../views/diagnostics/RemoteSupportRequestView.vue"),
						},
					],
				},
				{
					path: "analytics",
					name: "analytics",
					component: () =>
						import("../views/metrics/AnalyticsView.vue"),
					redirect: {
						name: "machine-metrics",
					},
					children: [
						{
							path: "machine",
							name: "machine-metrics",
							component: () =>
								import("../views/metrics/MachineMetricsView.vue"),
						},
						{
							path: "field",
							name: "field-metrics",
							component: () =>
								import("../views/metrics/FieldMetricsView.vue"),
						},
						{
							path: "org",
							name: "org-metrics",
							component: () =>
								import("../views/metrics/OrgMetricsView.vue"),
						},
					],
				},
				{
					path: "/resource-documents",
					name: "resource-documents",
					component: ResourceDocumentsView,
				},
				{
					path: "settings",
					name: "settings",
					component: () => import("../views/SettingsView.vue"),
				},
				{
					path: "failover-info",
					name: "failover-info",
					component: () => import("../views/FailoverInfoView.vue"),
				},
				{
					path: "switch-organization",
					name: "switch-organization",
					component: () =>
						import("../views/SwitchOrganizationView.vue"),
					beforeEnter: [isUserHasPermission],
				},
				{
					path: `fields/${matchUUID("fieldId")}?/${matchUUID("missionId")}?`,
					name: "fields",
					component: () => import("../views/FieldsView.vue"),
				},
				{
					path: `mission-planned-paths/${matchUUID("missionId")}`,
					name: "mission-planned-paths",
					component: () =>
						import("../views/MissionPlannedPathsView.vue"),
					props: true,
				},
				{
					path: "missions",
					name: "missions",
					redirect: { name: "create-mission" },
				},
				{
					path: "missions/create",
					name: "create-mission",
					component: () =>
						import("../views/missions/CreateMissionView.vue"),
					redirect: { name: "create-mission-type" },
					children: [
						{
							path: "type",
							name: "create-mission-type",
							component: () =>
								import("../views/missions/steps/CreateMissionTypeStep.vue"),
						},
						{
							path: "setup",
							name: "create-mission-setup",
							component: () =>
								import("../views/missions/steps/CreateMissionSetupStep.vue"),
						},
						{
							path: "field",
							name: "create-mission-field",
							component: () =>
								import("../views/missions/steps/CreateMissionFieldStep.vue"),
						},
						{
							path: "blocks",
							name: "create-mission-blocks",
							component: () =>
								import("../views/missions/steps/CreateMissionBlocksStep.vue"),
						},
					],
				},
				{
					path: `implements/${matchUUID("implementId")}?`,
					name: "implements",
					component: () => import("../views/ImplementsView.vue"),
				},
				{
					path: `implements/new`,
					name: "new-implement",
					component: () => import("../views/NewImplementView.vue"),
				},
				{
					path: `implements/${matchUUID("implementId")}/edit`,
					name: "edit-implement",
					component: () => import("../views/EditImplementView.vue"),
				},
				{
					path: "users",
					name: "users",
					component: () => import("../views/UsersView.vue"),
				},

				{
					path: "settings",
					name: "settings",
					component: () => import("../views/SettingsView.vue"),
				},
				{
					path: "support-chat",
					name: "support-chat",
					component: () => import("../views/SupportChatView.vue"),
				},
				{
					path: "",
					redirect: { name: "home" },
				},
				{
					path: "/import-field",
					name: "import-field",
					component: () => import("@/views/ImportFieldView.vue"),
					beforeEnter: () => {
						initDataExchangeServiceClient();
					},
				},
				{
					path: "/import-johndeere",
					name: "import-johndeere",
					component: () => import("@/views/ImportJohnDeereView.vue"),
					beforeEnter: async (to, from, next) => {
						const guard = johnDeereAuthGuard(
							getApiUrl(
								getEnvironmentSetting(),
								getApiVersionSetting(),
							),
						);
						const result = await guard(to, from, next);
						if (result) {
							next();
						}
					},
				},
				{
					path: "/import-farmengage",
					name: "import-farmengage",
					component: () => import("@/views/ImportFarmEngageView.vue"),
					beforeEnter: async () => {
						if (isAuthenticated()) {
							sessionStorage.removeItem(
								"farmengage_oauth_portal_flow",
							);
							return true;
						}
						sessionStorage.setItem(
							"farmengage_oauth_portal_flow",
							"true",
						);
						startFarmEngageAuthFlow();
						return false;
					},
				},
				{
					path: "farmengage/callback",
					name: "farmengage-callback",
					meta: { farmEngageCallbackVariant: "import" },
					component: () =>
						import("@/views/FarmEngageOAuthCallbackView.vue"),
					beforeEnter: async (to, from, next) => {
						const isPortalFlow =
							sessionStorage.getItem(
								"farmengage_oauth_portal_flow",
							) === "true";
						if (isPortalFlow) {
							sessionStorage.removeItem(
								"farmengage_oauth_portal_flow",
							);
							try {
								await authenticateFarmEngage();
								next({
									name: "import-farmengage",
								});
							} catch (error) {
								console.error(
									"Error during FarmEngage authentication:",
									error,
								);
								next({ name: "auth" });
							}
							return;
						}
						next();
					},
				},
			],
		},
		{
			path: "/callback",
			name: "callback",
			component: () => {},
			beforeEnter: async (to, from, next) => {
				await callbackHandler(
					to,
					from,
					next,
					getApiUrl(getEnvironmentSetting(), getApiVersionSetting()),
					"/import-johndeere",
				);
			},
		},
		{
			path: "/:pathMatch(.*)*",
			redirect: { name: "home" },
		},
	],
});
router.beforeResolve(async () => {
	const environment = getEnvironmentSetting();
	const apiVersion = getApiVersionSetting();
	const apiUrl = getApiUrl(environment, apiVersion);
	initClient(false, "", "", apiUrl);
	initFlightpathServiceClient(apiUrl);
	initMachineCertificateClient(apiUrl);
});

router.beforeEach(async to => {
	function redirect() {
		if (
			to.name !== "auth" &&
			to.name !== "forgot-password" &&
			to.name !== "docs" &&
			to.name !== "farmengage-callback"
		) {
			return {
				name: "auth",
				query: { redirect: to.name?.toString() },
			};
		}
	}

	try {
		const { apiVersion, organizationId } = useSettings();

		if (to.name === "fields") {
			const queryOrganizationId = allowGuidInQueryParam(
				to.query.organizationId,
			);
			const hasOverrideQueryParams = queryOrganizationId !== undefined;

			if (hasOverrideQueryParams) {
				const isEventGroup = await isUserGroupsAuthorized(
					userGroupPermissionsSettings.allowedGroupsToEvents ?? [
						"FlightPathAdmin",
					],
				);

				const remainingQuery = { ...to.query };
				if (isEventGroup && apiVersion.value === "Prod") {
					if (queryOrganizationId != organizationId.value) {
						organizationId.value =
							queryOrganizationId ?? organizationId.value;
					}

					if (organizationId !== undefined && organizationId.value) {
						await setOrganizationId(organizationId.value);
					}
				} else {
					// prevent to load the mission
					delete remainingQuery.missionId;
				}
				// Remove the query parameters after processing to prevent them from affecting other pages
				delete remainingQuery.organizationId;
				return {
					path: to.path,
					hash: to.hash,
					query: remainingQuery,
					replace: true,
				};
			}
		}

		if (
			await isUserGroupsAuthorized(
				userGroupPermissionsSettings.allowedGroupsToSwitchOrganization ?? [
					"FlightPathAdmin",
				],
			)
		) {
			if (
				!(await setOrganizationId(
					organizationId.value || useCustomerId(),
				))
			)
				return redirect();
		} else {
			if (!(await setCustomerId(false))) return redirect();
		}

		return;
	} catch (e: unknown) {
		redirect();
	}
});

export default router;
